<template>
  <div>
    <div class="bonus-table-header">
      <Border class="!w-1/3" radius="4px" borderWidth="2px">
        <div class="date-btn w-full" :class="{ active: activeDropdown === 1 }" @click="toggleDropdown(1)">
          <span class="btn-text">{{ dateOptions[selectedDate1].label }}</span>
          <span :class="['arrow', activeDropdown === 1 ? 'up' : 'down']"></span>
        </div>
      </Border>
      <Border class="!w-1/3" radius="4px" borderWidth="2px">
        <div class="date-btn w-full" :class="{ active: activeDropdown === 2 }" @click="toggleDropdown(2)">
          <span class="btn-text">{{ typeOptions[selectedType].label }}</span>
          <span :class="['arrow', activeDropdown === 2 ? 'up' : 'down']"></span>
        </div>
      </Border>
      <Border class="!w-1/3" radius="4px" borderWidth="2px">
        <div class="date-btn w-full" :class="{ active: activeDropdown === 3 }" @click="toggleDropdown(3)">
          <span class="btn-text">{{ vendorOptions[selectedDate3]?.name || t('relatorios.bettingRecords.allDetails') }}</span>
          <span :class="['arrow', activeDropdown === 3 ? 'up' : 'down']"></span>
        </div>
      </Border>
    </div>

    <!-- 下拉内容区域，独立块级展示 -->
    <div v-if="activeDropdown === 1" class="dropdown-panel">
      <button v-for="(item, idx) in dateOptions" :key="item.value"
        :class="['option-btn', { active: idx === selectedDate1 }]" @click="selectDate(idx, 1)">
        {{ item.label }}
      </button>
    </div>
    <div v-if="activeDropdown === 2" class="dropdown-panel grid-panel">
      <button v-for="(item, idx) in typeOptions" :key="item.value"
        :class="['option-btn', { active: idx === selectedType }]" @click="selectType(idx)">
        {{ item.label }}
      </button>
    </div>
    <div v-if="activeDropdown === 3" class="dropdown-panel">
      <button v-for="(item, idx) in vendorOptions" :key="item.id || 'all'"
        :class="['option-btn', { active: idx === selectedDate3 }]" @click="selectVendor(idx)">
        {{ item.name }}
      </button>
    </div>

    <!-- 数据展示区域 -->
    <div v-if="datalist.length === 0 && !loading" class="empty-data">
      <div class="flex justify-center items-center">
        <img :src="getAssetsFile('home/No_record@2x.png')" alt=""  class="max-h-[84px] max-w-[106.5px] w-auto h-auto">
      </div>
      <div class="empty-text">{{ t('relatorios.bettingRecords.noRecords') }}</div>
    </div>

    <div v-else class="record-list">
      <div class="record-item" v-for="item in datalist" :key="item.id">
        <!-- 第一排：游戏类型和输赢金额 -->
        <div class="record-header">
          <span class="record-title">{{ item.gameType }}</span>
          <span class="record-amount" :class="{ positive: item.winLossAmount > 0, negative: item.winLossAmount < 0 }">
            {{ item.winLossAmount > 0 ? '+' : '' }}{{ item.winLossAmount }}
          </span>
        </div>
        <!-- 第二排：游戏名称和状态 -->
        <div class="record-middle">
          <span>{{ item.gameName }}</span>
          <div class="color-[#4FB5F1] text-[13px] pr-5px" v-if="item.status == 0">{{
            t('relatorios.bettingRecords.status.pending') }}</div>
          <div class="color-[#4FC796] text-[13px] pr-5px" v-if="item.status == 1">{{
            t('relatorios.bettingRecords.status.completed') }}</div>
          <div class="color-[#EA443A] text-[13px] pr-5px" v-if="item.status == 2">{{
            t('relatorios.bettingRecords.status.cancelled') }}</div>
        </div>
        <!-- 第三排：时间和订单号 -->
        <div class="record-footer">
          <span class="record-time color-[#49AC90]">{{ formatTime(item.betTime) }}</span>
          <span class="record-id">
            {{ item.orderId }}
            <img :src="getAssetsFile('home/copy.png')" alt="" class="icon-copy" @click="copy(item.orderId)" title="复制">
          </span>
        </div>
      </div>

      <!-- 加载更多提示 -->
      <div v-if="loading" class="loading-more">
        <div class="loading-spinner"></div>
        <span>{{ t('relatorios.loading') }}</span>
      </div>

      <!-- 没有更多数据提示 -->
      <div v-if="!hasMore && datalist.length > 0" class="no-more">
        {{ t('relatorios.bettingRecords.noMoreData') }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Border } from "@/components/Border"
import { ref, onMounted, onUnmounted, computed } from 'vue'
import * as GameApi from '@/api/game'
import { useClipboard } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import { getAssetsFile } from '@/utils/pub-use'
import { formatDateTime, getStartOfToday, getStartOfDaysAgo } from '@/utils/date'

const { t } = useI18n()

const dateOptions = [
  { label: t('relatorios.bettingRecords.today'), value: getStartOfToday() },
  { label: t('relatorios.bettingRecords.last3Days'), value: getStartOfDaysAgo(2) },
  { label: t('relatorios.bettingRecords.last5Days'), value: getStartOfDaysAgo(4) },
  { label: t('relatorios.bettingRecords.last7Days'), value: getStartOfDaysAgo(6) },
]

const typeOptions = [
  { label: t('relatorios.bettingRecords.allDetails'), value: '' },
  { label: t('relatorios.bettingStatistics.slots'), value: 'SLOTS' },
  { label: t('relatorios.bettingStatistics.casino'), value: 'Casino' },
  { label: t('relatorios.bettingStatistics.fishing'), value: 'FISH' },
  { label: t('relatorios.bettingStatistics.chess'), value: 'CHESS' },
  { label: t('relatorios.bettingStatistics.hall'), value: 'HALL' },
  { label: t('relatorios.bettingStatistics.videoGame'), value: 'VIDEO_GAME' },
  { label: t('relatorios.bettingStatistics.live'), value: 'LIVE' },
  { label: t('relatorios.bettingStatistics.arcades'), value: 'ARCADES' },
  // 可扩展
]

const filses = ref<any[]>([])

// 计算属性：厂商选项，包含"所有"选项
const vendorOptions = computed(() => {
  return [
    { id: 'all', name: t('relatorios.bettingRecords.allDetails') }, // 所有选项
    ...filses.value
  ]
})

const selectedDate1 = ref(0)
const selectedDate3 = ref(0)
const selectedType = ref(0)
const activeDropdown = ref<number | null>(null)
const datalist = ref<any[]>([])
const loading = ref(false)
const hasMore = ref(true)
const total = ref(0)

const params = ref({
  pageNo: 1,
  pageSize: 16, // 改为16条
  betTime: dateOptions[0].value,
  gameType: typeOptions[0].value,
  customVendorId: null,
})

const { copy: copyToClipboard } = useClipboard()

function toggleDropdown(idx: number) {
  activeDropdown.value = activeDropdown.value === idx ? null : idx
}

function selectDate(idx: number, dropdownIdx: number) {
  if (dropdownIdx === 1) {
    selectedDate1.value = idx
    params.value.betTime = dateOptions[idx].value
    resetAndFetch()
  }
  activeDropdown.value = null
}

function selectType(idx: number) {
  selectedType.value = idx
  params.value.gameType = typeOptions[idx].value
  resetAndFetch()
  activeDropdown.value = null
}

function selectVendor(idx: number) {
  selectedDate3.value = idx
  const selectedVendor = vendorOptions.value[idx]
  // 如果是"所有"选项，传空字符串；否则传厂商ID
  params.value.customVendorId = selectedVendor.id === 'all' ? '' : `${selectedVendor.id}`
  resetAndFetch()
  activeDropdown.value = null
}

// 重置并重新获取数据
function resetAndFetch() {
  params.value.pageNo = 1
  datalist.value = []
  hasMore.value = true
  fetchData()
}

// 获取数据
function fetchData() {
  if (loading.value || !hasMore.value) return

  loading.value = true

  GameApi.bettingFlow({
    ...params.value,
    betTime: formatDateTime(params.value.betTime),
  }).then(res => {
    const newData = res.data?.list || []
    total.value = res.data?.total || 0

    if (params.value.pageNo === 1) {
      // 第一页，直接替换数据
      datalist.value = newData
    } else {
      // 后续页面，合并数据
      datalist.value = [...datalist.value, ...newData]
    }

    // 判断是否还有更多数据
    hasMore.value = datalist.value.length < total.value
    loading.value = false
  }).catch(() => {
    loading.value = false
  })
}

// 加载下一页
// function loadMore() {
//   if (loading.value || !hasMore.value) return

//   params.value.pageNo++
//   fetchData()
// }

// 滚动监听
function handleScroll() {
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop
  const windowHeight = window.innerHeight
  const documentHeight = document.documentElement.scrollHeight

  // 距离底部100px时触发加载
  if (scrollTop + windowHeight >= documentHeight - 100) {
    loadMore()
  }
}

function formatTime(val: number | string) {
  return formatDateTime(Number(val))
}

function copy(text: string) {
  copyToClipboard(text)
    ; (window as any).$toast(t('relatorios.bettingRecords.copySuccess'))
}

onMounted(() => {
  const params = {
    gameType: '',
  }
  GameApi.thirdPartygetGameVendors(params).then(res => {
    filses.value = res.data || []
  })
  fetchData()
  window.addEventListener('scroll', handleScroll)
})
// }

// 加载下一页
function loadMore() {
  if (loading.value || !hasMore.value) return

  params.value.pageNo++
  fetchData()
}

// 滚动监听
// function handleScroll() {
//   const scrollTop = window.pageYOffset || document.documentElement.scrollTop
//   const windowHeight = window.innerHeight
//   const documentHeight = document.documentElement.scrollHeight

//   // 距离底部100px时触发加载
//   if (scrollTop + windowHeight >= documentHeight - 100) {
//     loadMore()
//   }
// }

// function formatTime(val: number | string) {
//   return formatDateTime(Number(val))
// }

// function copy(text: string) {
//   copyToClipboard(text)
//     ; (window as any).$toast(t('relatorios.bettingRecords.copySuccess'))
// }

// onMounted(() => {
//   // GameApi.thirdPartygetGameVendors().then(res => {
//   //   filses.value = res.data || []
//   // })
//   fetchData()
//   window.addEventListener('scroll', handleScroll)
// })

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>
<style scoped>
.bonus-table-header {
  max-width: 100%;
  margin: 15px 15px 0 15px;
  display: flex;
  align-items: center;
  position: relative;
  gap: 6px;
}

.date-btn {
  background: transparent;
  /* border: 1px solid #40b665; */
  width: 100%;
  color: #fff;
  border-radius: 8px;
  padding: 4px 12px 4px 12px;
  font-size: 11px;
  font-weight: 500;
  outline: none;
  cursor: pointer;
  text-wrap: nowrap;
  margin-right: 8px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.btn-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}
.arrow {
  display: inline-block;
  margin-left: 6px;
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid #40b665;
  transition: transform 0.2s;
}

.arrow.up {
  transform: rotate(180deg);
}

.arrow.down {
  transform: rotate(0deg);
}

.dropdown-panel {
  width: 100%;
  background: #02523d;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 8px;
  margin-bottom: 10px;
}

.grid-panel {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  padding: 10px 8px;
}

.option-btn {
  background: transparent;
  border: 1px solid #40b665;
  color: #40b665;
  border-radius: 8px;
  padding: 5px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  min-width: 50px;
  text-align: center;
  word-break: break-all;
  min-height: 40px;
}

.option-btn.active,
.option-btn:hover {
  border: 1px solid #40b665;
  background: linear-gradient(90deg, #40b665 0%, #1ecb7b 100%);
  color: #fff;

}

.empty-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 115px;
}

.empty-text {
  color: #ADB1B0;
  font-size: 11px;
  margin-top: 21px;
}

.record-list {
  padding: 0 10px;
  margin-top: 15px;
}

.record-item {
  background: #085b3c;
  border-radius: 8px;
  margin-bottom: 12px;
  padding: 12px 14px 10px 14px;
  color: #fff;
}

.record-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
}

.record-title {
  font-weight: 500;
  font-size: 12px;
}

.record-amount {
  font-weight: bold;
  font-size: 13px;
}

.record-amount.positive {
  color: #40b665;
}

.record-amount.negative {
  color: #ff4d4f;
}

.record-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  font-size: 11px;
  color: #b2e0c6;
}

.record-id {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #49AC90;
}

.icon-copy {
  display: inline-block;
  width: 16px;
  height: 16px;
  cursor: pointer;
  margin-left: 4px;
}

.record-middle {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  margin-bottom: 2px;
  font-size: 11px;
  color: #fff;
}

.record-status {
  font-size: 13px;
}

.loading-more {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: #60B289;
  font-size: 14px;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #40b665;
  border-top: 2px solid transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 8px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.no-more {
  text-align: center;
  padding: 20px;
  color: #60B289;
  font-size: 14px;
}
</style>
